import { useState, useEffect } from 'react';
import { X, Key, Shield, AlertTriangle, Save, Loader2, Bot, Camera } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { CustomDropdown } from '../../components/CustomDropdown ';
import SingleSelectApiDropdown from '../../components/SingleSelectApiDropdown';

interface AddCredentialModalProps {
    open: boolean;
    onClose: () => void;
    assetId: number | null;
    clientId: any;
    isCamera?: boolean;
    initialAgentId?: number | null;
}

type CredentialType = 'api' | 'snmp' | 'camera';
type SnmpVersion = 'v1' | 'v2c' | 'v3';

const AddCredentialModal: React.FC<AddCredentialModalProps> = ({
    open,
    onClose,
    assetId,
    clientId,
    isCamera = false,
    initialAgentId = null
}) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentCredentialId, setCurrentCredentialId] = useState<number | null>(null);
    const [agent, setAgent] = useState<any>(null);

    const initialFormState = {
        credential_name: '',
        description: '',
        credential_type: (isCamera ? 'camera' : 'snmp') as CredentialType,
        username: '',
        password: '',
        api_key: '',
        snmp_version: 'v2c' as SnmpVersion,
        snmp_community: 'public',
        snmp_username: '',
        snmp_auth_protocol: '',
        snmp_auth_key: '',
        snmp_priv_protocol: '',
        snmp_priv_key: '',
        port: 161,
        make: ''
    };

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const fetchInitialAgent = async (id: number) => {
        try {
            const response = await baseapi.get(`/agents/${id}`);
            if (response.data.status) {
                setAgent(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching initial agent:", error);
        }
    };

    const resetForm = () => {
        // Always derive the default credential type from the latest isCamera prop
        setFormData({ ...initialFormState, credential_type: isCamera ? 'camera' : 'snmp' });
        setErrors({});
        setIsEditMode(false);
        setAgent(null);
        setCurrentCredentialId(null);
    };

    useEffect(() => {
        if (open) {
            // Apply correct default immediately when opened based on current prop
            setFormData(prev => ({ ...prev, credential_type: isCamera ? 'camera' : 'snmp' }));

            if (assetId) {
                checkExistingCredentials();
            }

            if (initialAgentId) {
                fetchInitialAgent(initialAgentId);
            }
        } else {
            resetForm();
        }
    }, [open, assetId, isCamera, initialAgentId]);

    const checkExistingCredentials = async () => {
        try {
            setFetching(true);
            const response = await baseapi.get(`/credentials`, {
                params: { query: JSON.stringify({ asset_id: assetId }) }
            });

            if (response.data.status && response.data.data?.length > 0) {
                const cred = response.data.data[0];
                let d: any = {};

                if (cred.credential_data) {
                    try {
                        d = typeof cred.credential_data === 'string'
                            ? JSON.parse(cred.credential_data)
                            : cred.credential_data;
                    } catch (e) {
                        console.error("Failed to parse credential_data:", e);
                    }
                }

                setFormData({
                    credential_name: cred.credential_name || '',
                    description: cred.description || '',
                    credential_type: (cred.credential_type as CredentialType) || (isCamera ? 'camera' : 'snmp'),
                    username: d.username || '',
                    password: d.password || d.api_secret || d.auth_password || '',
                    api_key: d.api_key || '',
                    snmp_version: d.snmp_version || 'v2c',
                    snmp_community: d.community || '',
                    snmp_username: d.snmp_username || '',
                    snmp_auth_protocol: d.auth_protocol || '',
                    snmp_auth_key: d.auth_password || '',
                    snmp_priv_protocol: d.priv_protocol || '',
                    snmp_priv_key: d.priv_password || '',
                    port: d.port || 161,
                    make: d.make || ''
                });

                if (cred.agent_id) {
                    fetchInitialAgent(cred.agent_id);
                }

                setCurrentCredentialId(cred.id);
                setIsEditMode(true);
            } else {
                // EXPLICIT RESET: Clear form properly if no credentials exist in DB for the asset
                resetForm();
            }
        } catch (error) {
            console.error("Error fetching credentials:", error);
        } finally {
            setFetching(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors: Record<string, string> = {};
        if (!formData.credential_name) newErrors.credential_name = "Name is required";
        if (!agent) newErrors.agent = "Agent is required";
        if (Object.keys(newErrors).length > 0) return setErrors(newErrors);

        try {
            setLoading(true);
            let credential_data = {};

            if (formData.credential_type === 'camera') {
                credential_data = { username: formData.username, password: formData.password, make: formData.make };
            } else if (formData.credential_type === 'api') {
                credential_data = { api_key: formData.api_key, api_secret: formData.password };
            } else if (formData.credential_type === 'snmp') {
                if (formData.snmp_version === 'v3') {
                    credential_data = {
                        snmp_version: 'v3',
                        snmp_username: formData.snmp_username,
                        auth_protocol: formData.snmp_auth_protocol,
                        auth_password: formData.snmp_auth_key,
                        priv_protocol: formData.snmp_priv_protocol,
                        priv_password: formData.snmp_priv_key,
                        make: formData.make
                    };
                } else {
                    credential_data = {
                        snmp_version: formData.snmp_version,
                        community: formData.snmp_community,
                        make: formData.make
                    };
                }
            }

            const payload = {
                credential_name: formData.credential_name,
                credential_type: formData.credential_type,
                client_id: Number(agent.client_id),
                asset_id: assetId,
                agent_id: Number(agent.id),
                credential_data
            };

            const res = isEditMode
                ? await baseapi.put(`/credentials/${currentCredentialId}`, payload)
                : await baseapi.post('/credentials', payload);

            if (res.data.status) {
                showSuccess(`Credential ${isEditMode ? 'updated' : 'added'}`);
                resetForm(); // Force a clear on save before closing
                onClose();
            }
        } catch (error: any) {
            showError(error.message || "Error saving credential");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={onClose} />
            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
                <div className="w-full max-w-2xl bg-gray-900 border border-orange-400/20 shadow-2xl rounded-xl text-white relative max-h-[90vh] overflow-y-auto custom-scroll">

                    <div className="sticky top-0 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800 px-6 py-4 z-20 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Key className="w-5 h-5 text-orange-400" />
                            <h2 className="text-lg font-bold">
                                {fetching ? 'Loading...' : (isEditMode ? 'Edit Credential' : 'Add Credential')}
                            </h2>
                        </div>
                        <button onClick={onClose} className="text-gray-100 hover:text-white p-1 rounded-full hover:bg-gray-800 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {fetching ? (
                        <div className="p-10 flex flex-col items-center justify-center text-gray-100">
                            <Loader2 className="w-8 h-8 animate-spin mb-2 text-orange-500" />
                            <p>Fetching data...</p>
                        </div>
                    ) : (
                        <div className="p-6 space-y-5">
                            <div>
                                <SingleSelectApiDropdown
                                    label="Select Agent"
                                    value={agent}
                                    onChange={(item) => {
                                        setAgent(item);
                                        if (item) setErrors(prev => ({ ...prev, agent: '' }));
                                    }}
                                    apiEndpoint="/agents"
                                    icon={Bot}
                                    placeholder="Search and select agent..."
                                    displayProperty="name"
                                    searchFields={['name']}
                                    required={true}
                                    additionalQueries={{ client_id: clientId, agent_type: 'probe' }}
                                    searchInputPlaceholder="Type agent name..."
                                />
                                {errors.agent && <p className="text-red-400 text-xs mt-1 flex items-center"><AlertTriangle className="w-3 h-3 mr-1" />{errors.agent}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-100 mb-1">Credential Name *</label>
                                    <input
                                        type="text"
                                        className={`w-full bg-gray-800 border ${errors.credential_name ? 'border-red-500' : 'border-gray-700'} rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none`}
                                        value={formData.credential_name}
                                        onChange={(e) => setFormData({ ...formData, credential_name: e.target.value })}
                                        placeholder="e.g. Office Camera"
                                    />
                                    {errors.credential_name && <p className="text-red-500 text-xs mt-1">{errors.credential_name}</p>}
                                </div>

                                {!isCamera && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-100 mb-1">Credential Type</label>
                                        <CustomDropdown
                                            data={[{ label: 'SNMP', value: 'snmp' }, { label: 'API Key', value: 'api' }]}
                                            selectedValue={formData.credential_type}
                                            onSelect={(val) => setFormData({ ...formData, credential_type: val as CredentialType })}
                                            width="w-full"
                                            buttonHeight={10}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="p-5 bg-gray-800/40 border border-gray-700/50 rounded-lg space-y-4">
                                {formData.credential_type === 'camera' && (
                                    <>
                                        <div className="flex items-center gap-2 text-orange-400 text-sm font-medium border-b border-gray-700 pb-2">
                                            <Camera className="w-4 h-4" /> Camera Access
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <input
                                                placeholder="Username"
                                                autoComplete="new-password"
                                                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                                                value={formData.username}
                                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                            />
                                            <input
                                                type="password"
                                                autoComplete="new-password"
                                                placeholder="Password"
                                                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            />
                                            <input
                                                placeholder="Make (e.g. Hikvision)"
                                                className="md:col-span-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                                                value={formData.make}
                                                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                                            />
                                        </div>
                                    </>
                                )}

                                {formData.credential_type === 'api' && (
                                    <>
                                        <div className="flex items-center gap-2 text-orange-400 text-sm font-medium border-b border-gray-700 pb-2">
                                            <Shield className="w-4 h-4" /> API Configuration
                                        </div>
                                        <input
                                            placeholder="API Key"
                                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                                            value={formData.api_key}
                                            onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                        />
                                        <input
                                            type="password"
                                            placeholder="API Secret"
                                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        />
                                    </>
                                )}

                                {formData.credential_type === 'snmp' && (
                                    <>
                                        <div className="flex items-center gap-2 text-orange-400 text-sm font-medium border-b border-gray-700 pb-2">
                                            <Shield className="w-4 h-4" /> SNMP Configuration
                                        </div>
                                        <CustomDropdown
                                            data={[{ label: 'v1', value: 'v1' }, { label: 'v2c', value: 'v2c' }, { label: 'v3', value: 'v3' }]}
                                            selectedValue={formData.snmp_version}
                                            onSelect={(val) => setFormData({ ...formData, snmp_version: val as SnmpVersion })}
                                        />
                                        {formData.snmp_version !== 'v3' ? (
                                            <input
                                                placeholder="Community String"
                                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                                                value={formData.snmp_community}
                                                onChange={(e) => setFormData({ ...formData, snmp_community: e.target.value })}
                                            />
                                        ) : (
                                            <div className="grid grid-cols-2 gap-3">
                                                <input placeholder="Username" className="col-span-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm" value={formData.snmp_username} onChange={(e) => setFormData({ ...formData, snmp_username: e.target.value })} />
                                                <input placeholder="Auth Protocol (MD5/SHA)" className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm" value={formData.snmp_auth_protocol} onChange={(e) => setFormData({ ...formData, snmp_auth_protocol: e.target.value })} />
                                                <input type="password" placeholder="Auth Password" className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm" value={formData.snmp_auth_key} onChange={(e) => setFormData({ ...formData, snmp_auth_key: e.target.value })} />
                                                <input placeholder="Priv Protocol (DES/AES)" className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm" value={formData.snmp_priv_protocol} onChange={(e) => setFormData({ ...formData, snmp_priv_protocol: e.target.value })} />
                                                <input type="password" placeholder="Priv Password" className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm" value={formData.snmp_priv_key} onChange={(e) => setFormData({ ...formData, snmp_priv_key: e.target.value })} />
                                            </div>
                                        )}
                                        <input
                                            placeholder="Make (e.g. Cisco, HP)"
                                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                                            value={formData.make}
                                            onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                                        />
                                    </>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                                <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-100 hover:text-white transition-colors">Cancel</button>
                                <button type="button" onClick={handleSubmit} disabled={loading} className="px-6 py-2 bg-orange-600 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-orange-700 transition-colors disabled:opacity-50">
                                    {loading ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                                    {isEditMode ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AddCredentialModal;